import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import TablePagination from "./TablePagination";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
}

export default function DataTable<T>({
  data,
  columns,
  totalRows,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  isLoading,
  onRowClick,
}: DataTableProps<T>) {
  const pageCount =
    totalRows && pagination ? Math.ceil(totalRows / pagination.pageSize) : -1;

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: onPaginationChange
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(pagination!) : updater;
          onPaginationChange(next);
        }
      : undefined,
    onSortingChange: onSortingChange
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(sorting!) : updater;
          onSortingChange(next);
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: !!pagination,
    manualSorting: !!sorting,
    // Sorted on the server: one column at a time, always in some order, A to Z on the first click.
    ...(sorting && { enableMultiSort: false, enableSortingRemoval: false, sortDescFirst: false }),
  });

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-cell">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="table-head">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      aria-sort={
                        sorting && header.column.getCanSort()
                          ? ARIA_SORT[header.column.getIsSorted() || "none"]
                          : undefined
                      }
                      className="px-3 py-3 text-left text-xs font-semibold text-white cursor-pointer select-none hover:text-white/80"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {sorting ? (
                          header.column.getCanSort() && (
                            <SortIcon direction={header.column.getIsSorted()} />
                          )
                        ) : (
                          <>
                            {header.column.getIsSorted() === "asc" && " ↑"}
                            {header.column.getIsSorted() === "desc" && " ↓"}
                          </>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-10 text-center text-gray-400 dark:text-gray-500"
                  >
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-10 text-center text-gray-400 dark:text-gray-500"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row.original)}
                    className={`h-12 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      onRowClick ? "cursor-pointer" : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 whitespace-nowrap">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && (
        <TablePagination
          totalRows={totalRows ?? data.length}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
        />
      )}
    </>
  );
}

const ARIA_SORT = { asc: "ascending", desc: "descending", none: "none" } as const;

function SortIcon({ direction }: { direction: false | "asc" | "desc" }) {
  const Icon =
    direction === "asc" ? ChevronUp : direction === "desc" ? ChevronDown : ChevronsUpDown;
  return <Icon className="h-3.5 w-3.5" aria-hidden="true" />;
}
